import { Link } from 'react-router-dom';

/* ── Types ── */
interface ServiceItem {
  id: string;
  color: 'green' | 'red';
  icon: React.ReactNode;
  tag: string;
  title: string;
  intro: string;
  items: { subtitle: string; desc: string }[];
}

/* ── Icon helpers ── */
const IcoWifi = () => (
  <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.111 16.404a5.5 5.5 0 0 1 7.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
  </svg>
);
const IcoCircuit = () => (
  <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" />
  </svg>
);
const IcoRobot = () => (
  <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 0 1-.657.643 48.39 48.39 0 0 1-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 0 1-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 0 0-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 0 1-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 0 0 .657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 0 1-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 0 0 5.427-.63 48.05 48.05 0 0 0 .582-4.717.532.532 0 0 0-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 0 0 .658-.663 48.422 48.422 0 0 0-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 0 1-.61-.58v0Z" />
  </svg>
);
const IcoChart = () => (
  <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5m.75-9 3-3 2.148 2.148A12.061 12.061 0 0 1 16.5 7.605" />
  </svg>
);
const IcoGrad = () => (
  <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 3.741-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" />
  </svg>
);
const IcoCart = () => (
  <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
  </svg>
);

/* ── Full services data ── */
const services: ServiceItem[] = [
  {
    id: 'iot',
    color: 'green',
    icon: <IcoWifi />,
    tag: 'IoT Solutions',
    title: 'IoT Solutions',
    intro: 'We design and deploy smart systems that connect people, devices, and environments.',
    items: [
      {
        subtitle: 'Smart Agriculture',
        desc: 'Automated irrigation, soil moisture monitoring, and livestock tracking systems to boost farm productivity and reduce water waste.',
      },
      {
        subtitle: 'Smart Homes & Buildings',
        desc: 'Affordable smart locks, intelligent lighting control, and energy monitoring for safer, more efficient homes and commercial buildings.',
      },
      {
        subtitle: 'Energy & Utilities',
        desc: 'IoT-enabled solar energy systems, smart prepaid meters, and grid monitoring for reliable, measurable power supply.',
      },
      {
        subtitle: 'Emergency & Safety Systems',
        desc: 'Connected fire alarms, flood and disaster early-warning systems, and community safety networks for rapid emergency response.',
      },
    ],
  },
  {
    id: 'electronics',
    color: 'red',
    icon: <IcoCircuit />,
    tag: 'Electronics',
    title: 'Electronics & Embedded Systems',
    intro: 'We bring ideas to life through electronics innovation — from concept to finished product.',
    items: [
      {
        subtitle: 'Custom Electronics Design',
        desc: 'End-to-end PCB design, schematic capture, prototyping, and small-batch manufacturing tailored precisely to client specifications.',
      },
      {
        subtitle: 'Repair & Maintenance',
        desc: 'Affordable, professional electronics repair services for households and businesses — reducing e-waste and saving costs.',
      },
      {
        subtitle: 'Electronics Training',
        desc: 'Hands-on workshops and structured courses in circuit design, soldering, embedded programming for students, engineers, and hobbyists.',
      },
    ],
  },
  {
    id: 'robotics',
    color: 'green',
    icon: <IcoRobot />,
    tag: 'Robotics',
    title: 'Robotics',
    intro: 'We build robots that empower education, industry, and everyday life across Africa.',
    items: [
      {
        subtitle: 'Educational Robotics Kits',
        desc: 'Affordable, curriculum-aligned robotic kits for primary schools, secondary schools and universities to make STEM tangible and exciting.',
      },
      {
        subtitle: 'Industrial Automation',
        desc: 'Custom robotic systems for manufacturing, product packaging, and logistics — increasing precision, throughput, and safety on the factory floor.',
      },
      {
        subtitle: 'Service Robots',
        desc: 'Autonomous robots designed for urban environments: security patrol, cleaning automation, and last-mile delivery solutions.',
      },
    ],
  },
  {
    id: 'consulting',
    color: 'red',
    icon: <IcoChart />,
    tag: 'Consulting',
    title: 'Consulting & Integration',
    intro: 'We help organizations adopt and scale smart technologies with confidence.',
    items: [
      {
        subtitle: 'IoT / Robotics Consulting',
        desc: 'Strategic guidance for businesses integrating automation and connected systems — technology selection, architecture design, and vendor evaluation.',
      },
      {
        subtitle: 'Smart City Solutions',
        desc: 'Traffic flow monitoring, AI-powered surveillance, and disaster prevention infrastructure for safer, more efficient urban environments.',
      },
      {
        subtitle: 'Cloud & Mobile Integration',
        desc: 'Seamlessly linking IoT sensors and robotic systems with web and mobile dashboards for real-time visibility and control from anywhere.',
      },
    ],
  },
  {
    id: 'training',
    color: 'green',
    icon: <IcoGrad />,
    tag: 'Training',
    title: 'Training & Capacity Building',
    intro: 'We invest in people to build Africa\'s next generation of technology innovators.',
    items: [
      {
        subtitle: 'Youth & Women Empowerment Programs',
        desc: 'Inclusive training initiatives in IoT, robotics, and electronics specifically designed to bring underrepresented groups into tech careers.',
      },
      {
        subtitle: 'Professional Certification Courses',
        desc: 'Partnering with universities and institutions to deliver accredited, industry-recognized technical skills certifications.',
      },
      {
        subtitle: 'Hackathons & Innovation Challenges',
        desc: 'Community-driven events that bring together teams to solve real local problems using technology — fostering collaboration and entrepreneurship.',
      },
    ],
  },
  {
    id: 'ecommerce',
    color: 'red',
    icon: <IcoCart />,
    tag: 'E-commerce',
    title: 'E-commerce',
    intro: 'We make innovation accessible through our online store — for everyone, everywhere.',
    items: [
      {
        subtitle: 'Robotics Kits',
        desc: 'Curated robotic kits for schools, hobbyists, engineers, and innovators — ready to build, program, and explore.',
      },
      {
        subtitle: 'IoT Devices & Components',
        desc: 'Sensors, microcontrollers, smart locks, smart meters, development boards and complete IoT starter kits shipped to your door.',
      },
      {
        subtitle: 'Training Packages',
        desc: 'Online self-paced courses, live workshop registrations, and full certification program bundles available for purchase online.',
      },
      {
        subtitle: 'Secure Payments & Delivery',
        desc: 'Reliable local and international payment options with delivery across Burundi, East Africa, and beyond.',
      },
    ],
  },
];

/* ── Service section component ── */
const ServiceSection = ({ svc, index }: { svc: ServiceItem; index: number }) => {
  const isGreen = svc.color === 'green';
  const accent = isGreen ? '#1aa534' : '#d2252e';
  const bgAccent = isGreen ? 'bg-[#1aa534]/8' : 'bg-[#d2252e]/8';
  const textAccent = isGreen ? 'text-[#1aa534]' : 'text-[#d2252e]';
  const borderAccent = isGreen ? 'border-[#1aa534]' : 'border-[#d2252e]';
  const iconBg = isGreen ? 'bg-[#1aa534]/10 text-[#1aa534]' : 'bg-[#d2252e]/10 text-[#d2252e]';
  const dotColor = isGreen ? 'bg-[#1aa534]' : 'bg-[#d2252e]';

  return (
    <section
      id={svc.id}
      className={`py-16 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} border-b border-slate-100`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header row */}
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            <div className={`mt-1 flex h-14 w-14 shrink-0 items-center justify-center rounded-xl ${iconBg}`}>
              {svc.icon}
            </div>
            <div>
              <span className={`inline-block rounded-full ${bgAccent} ${textAccent} px-3 py-1 text-[11px] font-bold uppercase tracking-widest`}>
                {svc.tag}
              </span>
              <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">{svc.title}</h2>
              <p className="mt-2 max-w-xl text-slate-500 leading-6">{svc.intro}</p>
            </div>
          </div>
          <Link
            to="/contact"
            className={`shrink-0 self-start rounded-lg border ${borderAccent} ${textAccent} px-5 py-2.5 text-sm font-semibold transition-all hover:bg-opacity-10 ${isGreen ? 'hover:bg-[#1aa534]/8' : 'hover:bg-[#d2252e]/8'}`}
          >
            Request this service →
          </Link>
        </div>

        {/* Detail cards */}
        <div className={`mt-10 grid gap-4 sm:grid-cols-2 ${svc.items.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
          {svc.items.map((item) => (
            <div
              key={item.subtitle}
              className={`rounded-xl border border-slate-100 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md border-t-2`}
              style={{ borderTopColor: accent }}
            >
              <div className="flex items-start gap-2 mb-3">
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dotColor}`} />
                <h3 className="text-sm font-semibold text-slate-800">{item.subtitle}</h3>
              </div>
              <p className="text-sm leading-6 text-slate-500 pl-4">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ServicesPage = () => {
  return (
    <div className="bg-white">

      {/* ── Page Header ── */}
      <div className="border-b border-slate-200 bg-white py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="section-label">Our Services</span>
              <h1 className="mt-2 text-3xl font-bold text-slate-900 sm:text-4xl">
                What BETRobionics Offers
              </h1>
              <p className="mt-2 text-slate-500 max-w-xl leading-6">
                Six comprehensive service areas — fully detailed, built for Africa, delivered with expertise.
              </p>
            </div>
            <Link to="/contact" className="btn-primary-lg shrink-0">
              Request a Quote
            </Link>
          </div>

          {/* Quick-jump anchors */}
          <div className="mt-8 flex flex-wrap gap-2">
            {services.map((svc) => (
              <a
                key={svc.id}
                href={`#${svc.id}`}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                  svc.color === 'green'
                    ? 'bg-[#1aa534]/10 text-[#1aa534] hover:bg-[#1aa534]/20'
                    : 'bg-[#d2252e]/10 text-[#d2252e] hover:bg-[#d2252e]/20'
                }`}
              >
                {svc.title}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* ── All service sections ── */}
      {services.map((svc, i) => (
        <ServiceSection key={svc.id} svc={svc} index={i} />
      ))}

      {/* ── Bottom CTA ── */}
      <section className="bg-slate-900 py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white">Not sure which service fits your needs?</h2>
          <p className="mt-4 text-slate-400 leading-7">
            Our team will assess your challenges and recommend the right combination of IoT,
            robotics, electronics, and training solutions for your organization.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="rounded-lg bg-[#1aa534] px-8 py-3.5 text-sm font-semibold text-white shadow hover:bg-[#158a2c] transition-colors">
              Talk to an Expert
            </Link>
            <a href="mailto:contact@betr.bi" className="rounded-lg border border-white/20 px-8 py-3.5 text-sm font-semibold text-slate-300 hover:border-white/40 hover:text-white transition-colors">
              contact@betr.bi
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
